"use client";
import React, { useState, useCallback, useRef } from "react";
import {
    Dialog,
    Box,
    useTheme,
    Typography,
    DialogContent,
    IconButton,
    Button,
} from "@mui/material";
import "./styles.css";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useSelector } from "react-redux";
import { useDropzone } from "react-dropzone";
import ToastService from "@/service/toaster.service";
import { toast } from "react-toastify";
import loanApplicationService from "@/service/loanApplication.service";
import { steps } from "@/constants/data.const";
import StepContent from "./components/StepContent";



const LoanAppForm = ({ open, onClose, venderdetail }) => {
    const [step, setStep] = useState(0);
    const [selectedOptions, setSelectedOptions] = useState({});
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [income, setIncome] = useState([30_000, 1_000_000]);
    const [purchasePrice, setPurchasePrice] = useState([200_000, 10_000_000]);
    const [deposite, setDeposite] = useState(30_000);
    const [expenses, setExpenses] = useState([1_000, 100_000]);
    const [openModal, setOpenModal] = useState(false);
    const [errors, setErrors] = useState({});
    const [errMessage, setErrMessage] = useState(null);
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
    });

    const vendorId = venderdetail._id;
    const customerId = useSelector((state) => state.auth.auth._id);

    // --- File dropzone handlers ---
    const onDrop = useCallback(
        (acceptedFiles) => {
            if (selectedFiles.length + acceptedFiles.length > 10) {
                toast.warning("You can only upload up to 10 files.");
                return;
            }
            setSelectedFiles((prev) => [...prev, ...acceptedFiles]);
        },
        [selectedFiles]
    );
    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });
    const handleRemoveFile = (idx) =>
        setSelectedFiles((files) => files.filter((_, i) => i !== idx));

    // --- Option select handler ---
    const handleOptionSelect = (qIndex, optIndex) => {
        setSelectedOptions((prev) => ({
            ...prev,
            [`step${step}-q${qIndex}`]: optIndex,
        }));
        setErrMessage(null);
        setErrors({});
    };

    // --- Input change & validation for step 4 ---
    const validateStep4 = (data) => {
        const errs = {};
        if (!data.firstName) errs.firstName = "Required";
        if (!data.lastName) errs.lastName = "Required";
        if (!data.email) errs.email = "Required";
        else if (
            !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(data.email)
        )
            errs.email = "Invalid email";
        if (!data.phone) errs.phone = "Required";
        else if (!/^\d{10,11}$/.test(data.phone))
            errs.phone = "10–11 digits";
        return errs;
    };
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const updated = { ...formData, [name]: value };
        setFormData(updated);
        setErrors(validateStep4(updated));
    };

    // 1️⃣ Radio‐question validation for steps 0 & 1
    const getStep1Errors = () => {
        const errs = {};
        steps[step].questions.forEach((_, i) => {
            if (selectedOptions[`step${step}-q${i}`] === undefined) {
                errs[`step${step}-q${i}`] = "This field is required";
            }
        });
        return errs;
    };

    // 2️⃣ Slider validation for step 2
    const getStep2Errors = () => {
        const errs = {};
        // check both lower and upper bounds of each range or single string range
        const ranges = [income, purchasePrice, deposite];
        ranges.forEach((range, i) => {
            if (typeof range === "string") {
                if (!range.trim()) {
                    errs[`step2-q${i}`] = "This field is required";
                }
            } else if (Array.isArray(range)) {
                if (range[0] === undefined || range[0] === "") {
                    errs[`step2-q${i}`] = "This field is required";
                }
            }
        });
        return errs;
    };

    // 3️⃣ Text‑field validation for step 3
    const getStep3Errors = () => {
        const errs = {};
        if (!formData.firstName) errs.firstName = "First name is required";
        if (!formData.lastName) errs.lastName = "Last name is required";
        if (!formData.email) errs.email = "Email is required";
        else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email))
            errs.email = "Enter a valid email";
        if (!formData.phone) errs.phone = "Phone is required";
        else if (!/^\d{10,11}$/.test(formData.phone))
            errs.phone = "Phone must be 10–11 digits";
        return errs;
    };

    // Unified validateStep
    const validateStep = () => {
        let newErr = {};
        if (step === 0 || step === 1) {
            newErr = getStep1Errors();
            if (Object.keys(newErr).length) {
                ToastService.warning("Answer all questions.");
                setErrMessage("Please answer all questions.");
            }
        } else if (step === 2) {
            newErr = getStep2Errors();
            console.log(newErr);
            if (Object.keys(newErr).length) {
                ToastService.warning("Set all ranges.");
                setErrMessage("Please set all required ranges.");
            }
        } else if (step === 3) {
            newErr = getStep3Errors();
            if (Object.keys(newErr).length) {
                ToastService.warning("Fill out all fields.");   
                setErrMessage("Please fill out all fields.");
            }
        }

        setErrors(newErr);
        return Object.keys(newErr).length === 0;
    };

    // Next‐step handler
    const handleNext = () => {
        if (validateStep()) {
            if (step < steps.length - 1) {
                setStep(step + 1);
                setErrMessage(null);
            } else {
                handleSubmit();
            }
        }
    };

    const handlePrevious = () => {
        if (step > 0) setStep(step - 1);
    };

    // --- Final submit ---
    const handleSubmit = async () => {
        const payload = {
            customer: customerId,
            vendor: vendorId,
            purpose_of_loan:
                steps[0].questions[0].options[selectedOptions["step0-q0"]],
            home_loan_situation:
                steps[0].questions[1].options[selectedOptions["step0-q1"]],
            credit_history:
                steps[0].questions[2].options[selectedOptions["step0-q2"]],
            employment_status:
                steps[1].questions[0].options[selectedOptions["step1-q0"]],
            number_of_applicants:
                steps[1].questions[1].options[selectedOptions["step1-q1"]] ===
                    "1 Applicant"
                    ? 1
                    : 2,
            source_of_income:
                steps[1].questions[2].options[selectedOptions["step1-q2"]],
            income_range: `${income[0]}K - ${income[1]}K`,
            expected_purchase_price: `${purchasePrice[0]}K - ${purchasePrice[1]}K`,
            deposit_amount: `${deposite}K`,
            expense_range: `${expenses[0]}K - ${expenses[1]}K`,
            first_name: formData.firstName,
            last_name: formData.lastName,
            email: formData.email,
            phone_number: formData.phone,
        };

        const form = new FormData();
        Object.entries(payload).forEach(([k, v]) => form.append(k, v));
        selectedFiles.forEach((f) => form.append("attachments", f));

        try {
            const res = await loanApplicationService.submitLoanApplication(form);
            if (res?.status) {
                ToastService.success("Submitted successfully!");
                setOpenModal(true);
                setTimeout(() => {
                    setOpenModal(false);
                    onClose();
                }, 2000);
            } else throw new Error(res.message || "Failed");
        } catch (err) {
            ToastService.error(err.message || "Submission failed");
        }
    };

    return (
        <>
            <Dialog
                open={open}
                maxWidth="md"
                fullWidth
                onClose={onClose}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        p: { xs: 1, sm: 3 },
                        backgroundColor: "#fff",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        minHeight: { xs: "auto", sm: "75vh" },
                        maxHeight: "85vh", // Prevent excessive height on large screens
                        overflowY: "auto", // Enable scroll only if content overflows
                    },
                }}
            >
                <Box className="dialog-header">
                    {step > 0 && (
                        <button className="dialog-back-btn" onClick={handlePrevious} disabled={step === 0}>
                            Back
                        </button>
                    )}

                    <Typography
                        className="dialog-title"
                        sx={{ fontSize: "2rem", fontWeight: "bold" }}
                    >
                        Preapproval application
                    </Typography>

                    {/* Optional empty box for spacing alignment on right */}
                    <Box sx={{ width: "70px" }} />
                </Box>
                {/* Main content */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    <StepContent
                        step={step}
                        questions={steps[step]?.questions || []}
                        selectedOptions={selectedOptions}
                        onSelect={handleOptionSelect}
                        income={income}
                        setIncome={setIncome}
                        purchasePrice={purchasePrice}
                        setPurchasePrice={setPurchasePrice}
                        deposite={deposite}
                        setDeposite={setDeposite}
                        expenses={expenses}
                        setExpenses={setExpenses}
                        formData={formData}
                        onInputChange={handleInputChange}
                        errors={errors}
                        selectedFiles={selectedFiles}
                        handleRemoveFile={handleRemoveFile}
                        getRootProps={getRootProps}
                        getInputProps={getInputProps}
                        isDragActive={isDragActive}
                    />
                </Box>

                        {
                    step<=2 ? ( 
                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                            variant="contained"
                            onClick={handleNext}
                            sx={{
                                backgroundColor: "#F7EE96",
                                color: "#000",
                                fontWeight: 600,
                                px: 4,
                                py: 1.5,
                                borderRadius: 2,
                                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                                "&:hover": {
                                    backgroundColor: "#f3e96e",
                                },
                            }}
                        >
                            NEXT →
                        </Button>
                    </Box>
                    ):(
                        <Box sx={{ display: "flex", justifyContent: "center"}}>
                            <Button
                                onClick={handleNext}
                                   sx={{
                                    background: "linear-gradient(90deg, #F4B400 70%, #E3C05D 100%)",
                                    borderRadius: "10px",
                                    px: 4,
                                    py: 1.5,
                                    fontWeight: "600",
                                    textTransform: "none",
                                    minWidth:"400px",
                                    color: "rgba(0, 0, 0, 0.87)",
                                    "&:hover": {
                                    background: "linear-gradient(90deg, #594ad1 0%, #947aff 100%)",
                                    },
                                }}
                                >
                            Submit
                        </Button>
                    </Box>
                    )
                }


                <IconButton
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        top: 16,
                        right: 16,
                        zIndex: 10,
                        color: "#000",
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </Dialog>
            <Dialog open={openModal} onClose={() => setOpenModal(false)}>
                <DialogContent sx={{ textAlign: "center", p: 4 }}>
                    <IconButton
                        onClick={() => setOpenModal(false)}
                        sx={{ position: "absolute", top: 8, right: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <CheckCircleOutlineIcon sx={{ fontSize: 60, color: "green" }} />
                    <Typography variant="h5" sx={{ mt: 2 }}>
                        Application Submitted!
                    </Typography>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default LoanAppForm;
